import { useState } from "react";
import {
  Coffee,
  Gift,
  Minus,
  Plus,
  Sparkles,
  Trash2,
  User,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Input,
  ScrollArea,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { cn, formatCurrency } from "@/lib/utils";
import { notify } from "@/lib/notify";
import { usePermissions } from "@/stores";
import type { CartItem, CartPromotion } from "@/stores/cart.store";
import type { Customer } from "@/types";
import { CompItemDialog } from "./CompItemDialog";

export interface CartPanelProps {
  cartItems: CartItem[];
  customer: Customer | null;
  orderType: "dine_in" | "takeaway";
  orderNotes: string;
  subtotal: number;
  discountAmount: number;
  promotion: CartPromotion | null;
  total: number;
  currencySymbol: string;
  getItemCount: () => number;
  setCustomer: (customer: Customer | null) => void;
  setOrderType: (type: "dine_in" | "takeaway") => void;
  setOrderNotes: (notes: string) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  setItemComped: (itemId: string, isComped: boolean, reason?: string) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  onCheckout: () => void;
  onCustomerClick: () => void;
  isMobile?: boolean;
}

export function CartPanel({
  cartItems,
  customer,
  orderType,
  orderNotes,
  subtotal,
  discountAmount,
  promotion,
  total,
  currencySymbol,
  getItemCount,
  setCustomer,
  setOrderType,
  setOrderNotes,
  updateItemQuantity,
  setItemComped,
  removeItem,
  clearCart,
  onCheckout,
  onCustomerClick,
  isMobile = false,
}: CartPanelProps) {
  const [compItemId, setCompItemId] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const canCompItems =
    hasPermission("orders.comp") || hasPermission("orders.create");

  const handleCompSubmit = (reason: string) => {
    if (!compItemId) {
      return;
    }

    setItemComped(compItemId, true, reason);
    setCompItemId(null);
    notify.success("Item comped");
  };

  const handleUncomp = (itemId: string) => {
    setItemComped(itemId, false);
    notify.success("Comp removed");
  };

  return (
    <>
      {!isMobile && (
        <div className="flex items-center justify-between bg-sidebar px-5 py-4 text-sidebar-foreground">
          <span className="text-sm font-semibold uppercase tracking-wider">
            Current Order
          </span>
          <span className="text-xs text-sidebar-foreground/70">
            {getItemCount()} items
          </span>
        </div>
      )}

      <div
        className={cn(
          "space-y-3 border-b bg-card",
          isMobile ? "px-4 py-3" : "px-5 py-4",
        )}
      >
        {customer ? (
          <div className="flex items-center justify-between rounded-md border bg-background px-3 py-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{customer.name}</p>
                <p className="text-xs text-muted-foreground">
                  {customer.loyaltyPoints} points
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setCustomer(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onCustomerClick}
            className="w-full rounded-md border bg-background px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
          >
            Link Customer (optional)
          </button>
        )}

        <Tabs
          value={orderType}
          onValueChange={(v) => setOrderType(v as "dine_in" | "takeaway")}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="dine_in">Dine In</TabsTrigger>
            <TabsTrigger value="takeaway">Takeaway</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <ScrollArea className="flex-1">
        {cartItems.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center p-6 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Coffee className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">No items yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Tap a product to add it
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {cartItems.map((item) => (
              <CartLine
                key={item.id}
                item={item}
                currencySymbol={currencySymbol}
                isMobile={isMobile}
                canComp={canCompItems}
                onComp={() => setCompItemId(item.id)}
                onUncomp={() => handleUncomp(item.id)}
                onRemove={() => removeItem(item.id)}
                onIncrease={() => updateItemQuantity(item.id, item.quantity + 1)}
                onDecrease={() => updateItemQuantity(item.id, item.quantity - 1)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
      <div
        className={cn("border-t bg-card", isMobile ? "px-4 py-3" : "px-5 py-4")}
      >
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal</span>
            <span className="font-medium text-foreground">
              {formatCurrency(subtotal, currencySymbol)}
            </span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-green-600">
              <span className="flex items-center gap-1.5 truncate">
                {promotion?.type === "happy_hour" && (
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                )}
                <span className="truncate">
                  {promotion?.name ?? "Discount"}
                </span>
              </span>
              <span className="shrink-0">
                -{formatCurrency(discountAmount, currencySymbol)}
              </span>
            </div>
          )}
          <Separator className="my-2" />
          <div className="flex items-baseline justify-between">
            <span className="text-base font-semibold">Total</span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(total, currencySymbol)}
            </span>
          </div>
        </div>
        <div className="mt-3">
          <Input
            placeholder="Order notes (e.g., allergies, special requests)..."
            value={orderNotes}
            onChange={(e) => setOrderNotes(e.target.value)}
            className="text-sm"
          />
        </div>
        <div className={cn("mt-4", isMobile ? "flex gap-2" : "space-y-2")}>
          <Button
            variant="outline"
            className={cn(
              "border-primary/40 text-primary hover:bg-primary/5",
              isMobile ? "flex-1" : "w-full",
            )}
            onClick={clearCart}
            disabled={cartItems.length === 0}
          >
            Clear
          </Button>
          <Button
            className={isMobile ? "flex-[2]" : "w-full"}
            size={isMobile ? "default" : "lg"}
            onClick={onCheckout}
            disabled={cartItems.length === 0}
          >
            {isMobile
              ? "Checkout"
              : `Place Order — ${formatCurrency(total, currencySymbol)}`}
          </Button>
        </div>
      </div>
      <CompItemDialog
        open={!!compItemId}
        onOpenChange={(open) => {
          if (!open) {
            setCompItemId(null);
          }
        }}
        onConfirm={handleCompSubmit}
      />
    </>
  );
}

interface CartLineProps {
  item: CartItem;
  currencySymbol: string;
  isMobile: boolean;
  canComp: boolean;
  onComp: () => void;
  onUncomp: () => void;
  onRemove: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
}

function CartLine({
  item,
  currencySymbol,
  isMobile,
  canComp,
  onComp,
  onUncomp,
  onRemove,
  onIncrease,
  onDecrease,
}: CartLineProps) {
  return (
    <div
      className={cn(isMobile ? "p-3" : "p-4", item.isComped && "bg-green-50")}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p
              className={cn(
                "font-medium",
                item.isComped && "text-muted-foreground line-through",
              )}
            >
              {item.product.name}
            </p>
            {item.isComped && (
              <Badge
                variant="outline"
                className="border-green-500 text-xs text-green-700"
              >
                COMP
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{item.variant.name}</p>
          {item.selectedOptions.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {item.selectedOptions.map((opt) => (
                <Badge key={opt.id} variant="secondary" className="text-xs">
                  {opt.name}
                </Badge>
              ))}
            </div>
          )}
          {item.notes && (
            <p className="mt-1 text-xs text-muted-foreground">
              Note: {item.notes}
            </p>
          )}
          {item.isComped && item.compReason && (
            <p className="mt-1 text-xs text-green-600">
              Reason: {item.compReason}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canComp && !item.isComped && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onComp}
              title="Comp item"
            >
              <Gift className="h-4 w-4 text-green-600" />
            </Button>
          )}
          {item.isComped && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onUncomp}
              title="Remove comp"
            >
              <X className="h-4 w-4 text-green-600" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={onDecrease}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-8 text-center font-medium">{item.quantity}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={onIncrease}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <span
          className={cn(
            "font-medium",
            item.isComped && "text-muted-foreground line-through",
          )}
        >
          {item.isComped
            ? formatCurrency(item.unitPrice * item.quantity, currencySymbol)
            : formatCurrency(item.subtotal, currencySymbol)}
        </span>
      </div>
    </div>
  );
}
